import React, { useState, useEffect } from 'react';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useSetRecoilState } from 'recoil';
import { useHistory, useLocation } from 'react-router';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  ASRProviderCreationRequest,
  ASRProviderModel,
  ASRProviderSettingsType,
  ASRProviderUpdatingRequest,
  ProviderModel,
} from '../../../../gamma-api';
import { defaultApi } from '../../../apis';
import { ValidationErrors } from '../../../types';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../utils/urlUtil';
import { getTranslateFunc } from '../../../utils/stringUtil';
import { tryGetValidationErrors } from '../../../utils/errorUtils';
import { alertsSelectorAdd } from '../../../recoil';
import IbIcon from '../../../components/common/IbIcon';
import IbTypography from '../../../components/common/IbTypography';
import IbButton from '../../../components/common/IbButton';
import IbPageTitle from '../../../components/IbPageTitle';
import IbPagination from '../../../components/common/IbPagination';
import IbTable from '../../../components/common/IbTable';
import IbContextMenu, { IIbContextMenuItem } from '../../../components/common/IbContextMenu';
import IbTextHighlighter from '../../../components/common/IbTextHighlighter';
import IbTooltip from '../../../components/common/IbTooltip';
import { LARGE_PAGE_SIZE } from '../../../constants';

import {
  ASR_PROVIDERS_PAGE_SIZE,
  CONTENT_CLASS_NAME,
  MAIN_CLASS_NAME,
  NAME_CELL_CLASS_NAME,
  PAGINATION_CLASS_NAME,
  TOOLBAR_CLASS_NAME,
  TOOLTIP_MOUSE_DELAY,
  TRANSLATION_GROUP_PREFIX,
  TYPE_CELL_CLASS_NAME,
} from './const';
import ConfirmModal from './ConfirmModal';
import AsrProviderModal from './AsrProviderModal';

const AsrProvidersPage: React.FC = () => {
  const location = useLocation();
  const { push } = useHistory();

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: ASR_PROVIDERS_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [llmProviders, setLlmProviders] = useState<ProviderModel[]>([]);
  const [asrProviders, setAsrProviders] = useState<ASRProviderModel[]>([]);

  const [llmProvidersLoading, setLlmProvidersLoading] = useState(true);
  const [asrProvidersLoading, setAsrProvidersLoading] = useState(true);
  const [asrProviderDeleting, setAsrProviderDeleting] = useState(false);

  const [asrProviderModalVisible, setAsrProviderModalVisible] = useState(false);

  const [contextAsrProvider, setContextAsrProvider] = useState<ASRProviderModel>();
  const [modalAsrProvider, setModalAsrProvider] = useState<ASRProviderModel>();
  const [deleteAsrProvider, setDeleteAsrProvider] = useState<ASRProviderModel>();

  const translate = getTranslateFunc(TRANSLATION_GROUP_PREFIX);

  const loadLlmProviders = async (withLoading: boolean) => {
    withLoading && setLlmProvidersLoading(true);

    try {
      const response = await defaultApi.searchProvidersGeneralProvidersGet(0, LARGE_PAGE_SIZE, true);
      setLlmProviders(response.data.items || []);
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    withLoading && setLlmProvidersLoading(false);
  };

  const loadAsrProviders = async (withLoading: boolean) => {
    withLoading && setAsrProvidersLoading(true);

    try {
      const response = await defaultApi.searchAsrProviders(
        (pagination.current || 1) - 1,
        ASR_PROVIDERS_PAGE_SIZE,
        true,
        searchText
      );
      setAsrProviders(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    withLoading && setAsrProvidersLoading(false);
  };

  useEffect(() => {
    loadLlmProviders(true).finally();
  }, []);

  useEffect(() => {
    loadAsrProviders(true).finally();
  }, [pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [searchText]);

  useUpdateEffect(() => {
    if (pagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [pagination.current]);

  const resetProviderModal = () => {
    setAsrProviderModalVisible(false);
    setModalAsrProvider(undefined);
  };

  const onCreateProviderClick = () => {
    setAsrProviderModalVisible(true);
  };

  const onProviderModalCancel = () => {
    resetProviderModal();
  };

  const onProviderCreate = async (
    request: ASRProviderCreationRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    try {
      await defaultApi.createAsrProvider(request);
      await loadAsrProviders(false);
      resetProviderModal();
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Failed to create'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onProviderUpdate = async (
    request: ASRProviderUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    if (!modalAsrProvider) return;

    try {
      await defaultApi.updateAsrProvider(modalAsrProvider.asr_provider_id, request);
      await loadAsrProviders(false);
      resetProviderModal();
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Failed to update'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteAsrProvider) {
      return;
    }

    setAsrProviderDeleting(true);

    try {
      await defaultApi.deleteAsrProvider(deleteAsrProvider.asr_provider_id);
    } catch (e) {
      addAlert({
        content: translate('Failed to delete'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
      // eslint-disable-next-line no-console
      console.error(e);
    }

    await loadAsrProviders(false);
    setAsrProviderDeleting(false);
    setDeleteAsrProvider(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteAsrProvider(undefined);
  };

  const onTableRowClick = (provider: ASRProviderModel, column: ColumnType<ASRProviderModel>) => {
    if (!column.title) {
      return;
    }

    setModalAsrProvider(provider);
    setAsrProviderModalVisible(true);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const renderNameCell = (provider: ASRProviderModel) => {
    const text = provider.name;

    return (
      <div className={NAME_CELL_CLASS_NAME}>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} title={text}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">
              <IbTextHighlighter highlightedText={searchText} text={text} />
            </IbTypography.Paragraph>
          </IbTypography>
        </IbTooltip>
      </div>
    );
  };

  const renderTypeCell = (provider: ASRProviderModel) => {
    const typeNames = {
      [ASRProviderSettingsType.Llm]: translate('Table: Type: LLM'),
      [ASRProviderSettingsType.OpenAiCompatible]: translate('Table: Type: OpenAI compatible'),
    };
    const text = typeNames[provider.settings.type];

    return (
      <div className={TYPE_CELL_CLASS_NAME}>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} title={text}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{text}</IbTypography.Paragraph>
          </IbTypography>
        </IbTooltip>
      </div>
    );
  };

  const renderContextMenu = (provider: ASRProviderModel) => {
    const onUpdateClick = () => {
      setModalAsrProvider(provider);
      setAsrProviderModalVisible(true);
    };

    const onDeleteClick = () => setDeleteAsrProvider(provider);

    const onMenuOpen = () => setContextAsrProvider(provider);

    const onMenuClose = () => setContextAsrProvider(undefined);

    const menuItems: IIbContextMenuItem[] = [];

    menuItems.push({
      icon: <IbIcon iconName="edit" />,
      text: translate('Context: Update'),
      onSelect: onUpdateClick,
    });

    menuItems.push({
      icon: <IbIcon iconName="delete" />,
      text: translate('Context: Delete'),
      onSelect: onDeleteClick,
    });

    return (
      <IbContextMenu
        menuItems={menuItems}
        visible={contextAsrProvider?.asr_provider_id === provider.asr_provider_id}
        onClose={onMenuClose}
      >
        <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onMenuOpen}></IbButton>
      </IbContextMenu>
    );
  };

  const renderToolbar = () => {
    return (
      <div className={TOOLBAR_CLASS_NAME}>
        <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreateProviderClick}>
          {translate('Toolbar: Create')}
        </IbButton>
      </div>
    );
  };

  const renderTable = () => {
    const columns: ColumnType<ASRProviderModel>[] = [
      {
        title: translate('Table: Name'),
        width: '60%',
        render: (_, provider) => renderNameCell(provider),
      },
      {
        title: translate('Table: Type'),
        width: '40%',
        render: (_, provider) => renderTypeCell(provider),
      },
      {
        title: '',
        render: (_, provider) => renderContextMenu(provider),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={asrProviders}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        loading={asrProvidersLoading}
        rowKey={(provider: ASRProviderModel) => provider.asr_provider_id}
        onRowClick={onTableRowClick}
      />
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        showSearchInput
        searchPlaceholder={translate('Search: Placeholder')}
        title={translate('Title')}
        onDebouncedSearch={onSearchTextChange}
      />
      <div className={CONTENT_CLASS_NAME}>
        {renderToolbar()}
        {renderTable()}
      </div>
      {(pagination.total ?? 0) > ASR_PROVIDERS_PAGE_SIZE && (
        <div className={PAGINATION_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={ASR_PROVIDERS_PAGE_SIZE}
            pageSize={ASR_PROVIDERS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <AsrProviderModal
        editAsrProvider={modalAsrProvider}
        llmProviders={llmProviders}
        llmProvidersLoading={llmProvidersLoading}
        visible={asrProviderModalVisible}
        onCancel={onProviderModalCancel}
        onCreate={onProviderCreate}
        onUpdate={onProviderUpdate}
      />
      <ConfirmModal.DeleteAsrProvider
        loading={asrProviderDeleting}
        visible={!!deleteAsrProvider}
        onCancel={onDeleteCancelButtonClick}
        onConfirm={onDeleteConfirmButtonClick}
      />
    </div>
  );
};

export default AsrProvidersPage;
